import { computed, onScopeDispose, reactive, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { assignOrEditCustomer } from "@/utils/vue-helpers/customer";
import BookingModule from "@/store/modules/booking";
import POSConfigModule from "@/store/modules/pos-config";
import CashFloatModule from "@/store/modules/cash-float";
import CustomersModule from "@/store/modules/customers";
import AppModule from "@/store/modules/app";
import { BookingStep } from "@/constants/booking";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { useOrderStore } from "@smeetz/order-store";
import { printBookingReceipt } from "@/utils/vue-helpers/booking";
import {
  getOrderLinkedWaiverIds,
  WaiverErrorReason,
} from "@/api/waivers/waivers";
import { getServicesErrorReason } from "@/utils/helpers/not-synced-retry";
import { logOrderActionError } from "@/utils/vue-helpers/order-action-errors.helper";
import { LogLevel, LogService } from "@/services/logging";
import { Delete as DeleteIcon } from "@element-plus/icons-vue";
import person from "@/assets/images/svg/person.svg";
import printer from "@/assets/images/svg/printer.svg";
import calculatorSvg from "@/assets/images/svg/calculator-sign.svg";

/**
 * Returns all necessary variables (objects and functions) used in AsideOrderSummary and can be used as well in CustomerBasket
 */
export function setupAsideOrderSummary() {
  const { t } = useI18n({});

  const showCancelTicketsModal = ref(false);
  const isOnline = ref(window.navigator.onLine);
  const isLongTitle = String(t("asideordersummary.title")).length > 15;

  const iconStyle =
    "width: 18px; height: 18px; filter: brightness(0) saturate(100%) contrast(170%) invert(100%);";
  const iconContent = person;
  const iconAlt = "user";

  const printIcon = printer;
  const printAlt = "print";

  const calcIcon = calculatorSvg;
  const calcAlt = "calculator";
  const calcIconStyle = "width: 30px; height: 30px;";

  const calcVisibility = ref(false);

  // computed property used to show removeButton loading if the removal process involves async operations
  const isRemoveButtonLoading = ref(false);

  const showCopyCheck = ref(false);

  ///////////////////////////////////
  const closeCalculator = () => {
    calcVisibility.value = !calcVisibility.value;
    BookingModule.setCalcValue("0");
  };

  const getCustomerFullName = computed<string>(() => {
    const orderStore = useOrderStore();
    const customer = CustomersModule.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return customer.fullName;
    }
    return t("asideordersummary.assign-customer");
  });

  const groupCurrencies = computed(() => {
    return POSConfigModule.groupCurrencies;
  });

  const currencies = computed(() => {
    const currencies = [];
    const startAmounts = CashFloatModule.cashFloat?.startAmounts;
    for (const currency in startAmounts) {
      const currencyInfo = groupCurrencies.value.find(
        (c) => c.label === currency
      );
      if (currencyInfo) {
        currencies.push(currencyInfo);
      }
    }
    return currencies;
  });

  const groupCurrency = computed(() => {
    return (
      currencies.value.find((c) => c.isDefault === 1) || currencies.value[0]
    );
  });

  const onClickCustomers = () => {
    assignOrEditCustomer();
  };

  function closeModal() {
    showCancelTicketsModal.value = false;
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      showCopyCheck.value = true;
      setTimeout(() => {
        showCopyCheck.value = false;
      }, 1500);
    });
  }

  function printBill() {
    printBookingReceipt({ isBill: true });
  }

  const showPrintBill = computed(() => {
    const unsupportedCountries = ["FR", "BE"];
    if (unsupportedCountries.includes(POSConfigModule.getGroupCountryCode)) {
      return false;
    }

    const orderMetaStore = useOrderMetaStore();
    if (orderMetaStore.step === BookingStep.CompleteOrder) {
      // Don't allow printing bill in Complete Order step
      return false;
    }

    const orderStore = useOrderStore();
    if (orderStore.selectedOrderSummary.orderLines.length === 0) {
      return false;
    }
    return true;
  });

  // ── Waiver section (CNT-540 / ORD-758) ──────────────────────────────────────
  // The aside shows a waiver section in the completed-order step for the groups
  // allowed to use waivers.
  const isWaiverAllowed = computed(() => {
    return (
      AppModule.debugMode ||
      POSConfigModule.groupId === 1 ||
      POSConfigModule.groupId === 19066 ||
      POSConfigModule.groupId === 19755 ||
      POSConfigModule.groupId === 19763 ||
      POSConfigModule.groupId === 19786 ||
      import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev"
    );
  });

  // ── Linked waivers ──────────────────────────────────────────────────────────
  // Kept per order in the order-meta store, so switching back to a completed
  // tab shows its last known count at once while a background refresh re-reads
  // it. The backend is the source of truth: the ids are only ever replaced by a
  // fetch (on completion, on order switch, when the link modal opens and after
  // each link/unlink), never incremented locally.
  const orderMetaStore = useOrderMetaStore();
  const { linkedWaiverIds, isInCompletedStep } = storeToRefs(orderMetaStore);
  const linkedCount = computed(() => {
    return linkedWaiverIds.value.length;
  });

  const selectedOrder = computed(() => useOrderStore().selectedOrder);

  // One fetch per order at a time: a refresh asked while the order's fetch is
  // still retrying through the sync window joins it instead of starting a
  // second retry loop.
  const inFlight = new Map<string, Promise<void>>();
  const loadingOrderIds = reactive(new Set<string>());
  const isLoadingCount = computed(() => {
    const orderId = selectedOrder.value?.id;
    return !!orderId && loadingOrderIds.has(orderId);
  });

  // Leaving the booking view unmounts the aside: a fetch still retrying stops.
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  function refreshLinkedWaivers(retries?: number): Promise<void> {
    const order = selectedOrder.value;
    if (!order || !isInCompletedStep.value || !isWaiverAllowed.value) {
      return Promise.resolve();
    }
    const { id: orderId, reference: orderReference } = order;
    const running = inFlight.get(orderId);
    if (running) return running;

    const fetching = (async () => {
      loadingOrderIds.add(orderId);
      try {
        const ids = await getOrderLinkedWaiverIds(
          orderId,
          retries,
          () => !disposed
        );
        orderMetaStore.setLinkedWaiverIds(orderId, ids);
      } catch (error) {
        // The section keeps its last known count or the default "waiver to
        // sign" text: no toast on a background read the cashier did not trigger.
        const reason = getServicesErrorReason(error);
        if (reason === WaiverErrorReason.OrderNotSynced) {
          LogService.info(
            `Warning reading linked waivers of order ${orderReference}: ${reason}`,
            { orderId },
            LogLevel.Warning
          );
        } else {
          logOrderActionError(
            `reading linked waivers of order ${orderReference}`,
            { orderId },
            error
          );
        }
      } finally {
        loadingOrderIds.delete(orderId);
        inFlight.delete(orderId);
      }
    })();
    inFlight.set(orderId, fetching);
    return fetching;
  }

  // Re-read whenever the selected order or its step changes: the aside
  // survives tab switches and the step is stored per order.
  watch(
    [isInCompletedStep, () => selectedOrder.value?.id],
    () => {
      refreshLinkedWaivers();
    },
    { immediate: true }
  );

  return {
    showCancelTicketsModal,
    isLongTitle,
    iconContent,
    iconAlt,
    iconStyle,
    printIcon,
    printAlt,
    getCustomerFullName,
    onClickCustomers,
    calcVisibility,
    calcIcon,
    calcIconStyle,
    calcAlt,
    closeCalculator,
    DeleteIcon,
    closeModal,
    groupCurrency, // FIXME:
    isRemoveButtonLoading,
    copyToClipboard,
    showCopyCheck,
    printBill,
    showPrintBill,
    isWaiverAllowed,
    linkedCount,
    linkedWaiverIds,
    isLoadingCount,
    refreshLinkedWaivers,
  };
}
